import * as React from "react";
import {
  AnimatedList,
  AvatarGroup,
  CardGrid,
  ChatLog,
  ComposerHint,
  DaySeparator,
  EmptyState,
  ListeningWave,
  Markdown,
  MessageRow,
  MessageTimestamp,
  SegmentedControl,
  SetupChecklist,
  SuggestedReplies,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "@aisquare/ui";
import { Inbox } from "lucide-react";
import { Section, VariantBlock } from "../lib/Section";

/**
 * One page for the two states every data-fed component spends real time in.
 *
 * The switcher is the point. A skeleton's whole job is to hold the footprint
 * the real content will occupy, and the only way to judge whether it does is
 * to flip between them and watch for movement — a static screenshot of a
 * skeleton next to a static screenshot of the loaded thing tells you nothing
 * about whether the page jumps between the two. Flip the control and nothing
 * below it should move except the fill.
 *
 * The third position is the one that is easy to skip and the one that carries
 * the bug: `loading` outranks `empty` everywhere in the library, because a
 * collection is empty-by-fetch and empty-by-arrival at the same instant, and
 * answering a request still in flight with "there is nothing here" invites the
 * user to leave a surface that was one tick from filling.
 */

type State = "loading" | "empty" | "loaded";

const STATE_OPTIONS = [
  { value: "loading", label: "Loading" },
  { value: "empty", label: "Empty" },
  { value: "loaded", label: "Loaded" },
];

/* ── fixtures ──────────────────────────────────────────────────────────── */

interface Row {
  id: string;
  label: string;
}

const ROWS: Row[] = ["Quest", "AI Note", "Podcast", "AI Video", "Summary", "Roadmap"].map(
  (label, i) => ({ id: String(i + 1), label }),
);

const card = (r: Row) => (
  <div className="space-y-3">
    <div className="aspect-[4/3] w-full rounded-2xl border border-border bg-muted" />
    <p className="text-sm font-medium text-card-foreground">{r.label}</p>
    <p className="text-xs text-muted-foreground">Updated 2 days ago</p>
  </div>
);

const NOTIFICATIONS = [
  { id: 1, content: "Atlas finished your roadmap." },
  { id: 2, content: "3 new members joined AI Studio One." },
  { id: 3, content: "Your podcast render is ready." },
  { id: 4, content: "Weekly digest is available." },
];

const MEMBERS = [
  { name: "Ada Lovelace" },
  { name: "Grace Hopper" },
  { name: "Alan Turing" },
  { name: "Katherine Johnson" },
];

const TASKS = [
  {
    id: "1",
    label: "Name your studio",
    description: "Give it something you'd say out loud.",
    completed: true,
  },
  {
    id: "2",
    label: "Invite a collaborator",
    description: "Studios get better with two.",
    completed: false,
  },
  {
    id: "3",
    label: "Publish your first experience",
    description: "Ship it rough. Polish later.",
    completed: false,
  },
];

const INVOICES = [
  { id: "INV-001", plan: "Studio Pro", period: "Aug 2026", amount: "$48.00" },
  { id: "INV-002", plan: "Studio Pro", period: "Jul 2026", amount: "$48.00" },
  { id: "INV-003", plan: "Studio Pro", period: "Jun 2026", amount: "$48.00" },
];

const ANSWER = `Here's the shape of it. The **skeleton** holds the same footprint the prose will,
so nothing under it moves when the tokens land.

- one line per paragraph row
- the last line runs short, the way real prose does`;

/* ── a labelled stage, so each component reads as its own specimen ─────── */

function Specimen({
  name,
  note,
  children,
  wide = false,
}: {
  name: string;
  note: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div
      // Tagged so the headless pass can measure each specimen's height across
      // the three states — a skeleton that does not hold its footprint shows
      // up here as a number that moves.
      data-pg-specimen={name}
      className={
        wide
          ? "col-span-full rounded-2xl border border-border bg-card p-4"
          : "rounded-2xl border border-border bg-card p-4"
      }
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          {name}
        </span>
        <span className="text-right text-[11px] leading-tight text-muted-foreground/70">
          {note}
        </span>
      </div>
      {children}
    </div>
  );
}

/* ── page ──────────────────────────────────────────────────────────────── */

export function ComponentStatesDemo() {
  const [state, setState] = React.useState<State>("loading");
  const loading = state === "loading";
  const empty = state === "empty";

  // Every collection reads from this: `loaded` gets the fixture, the other two
  // get nothing. That the LOADING column is also `[]` is exactly the point —
  // it is the same array the empty state sees, and only the flag separates
  // "not yet" from "nothing".
  const rows = state === "loaded" ? ROWS : [];
  const notifications = state === "loaded" ? NOTIFICATIONS : [];
  const members = state === "loaded" ? MEMBERS : [];
  const tasks = state === "loaded" ? TASKS : [];
  const invoices = state === "loaded" ? INVOICES : [];

  return (
    <Section
      id="states"
      title="Empty & loading states"
      description="Every data-fed component in the library answers two questions separately: the shape is known but the content is not (loading), and there is genuinely nothing here (empty). Flip the switcher and watch the geometry — a skeleton's job is to hold roughly the footprint the real content will occupy, so the page settles instead of lurching. It can only ever be approximate: the library cannot know how long your prose is, which is why every skeleton here takes a count and why the components that can remember default it to the last result size. Note that Loading and Empty are handed the SAME empty array; only the flag separates them, and loading always wins."
    >
      <VariantBlock
        label="The switcher"
        caption="one control drives every specimen below — flip it and look for movement, not for prettiness"
      >
        <div className="w-full max-w-5xl space-y-5">
          <div className="flex flex-wrap items-center gap-3">
            <SegmentedControl
              options={STATE_OPTIONS}
              value={state}
              onChange={(v) => setState(v as State)}
              semantics="radiogroup"
            />
            <p className="text-xs text-muted-foreground">
              {loading
                ? "Placeholders are aria-hidden; each region carries one aria-busy."
                : empty
                  ? "Same empty array as Loading — only the flag differs."
                  : "The fixture. Compare footprints against Loading."}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Specimen
              name="MessageRow · Markdown"
              note="line count follows the speaker; actions suppressed while loading"
            >
              {empty ? (
                // Being straight about it rather than showing the loaded cell
                // again: neither of these owns an empty state, and pretending
                // otherwise would teach the wrong lesson about the pair.
                <p className="text-sm italic text-muted-foreground">
                  No empty state, by design. A transcript omits the row entirely rather than
                  rendering an empty bubble, so the decision sits one level up. Markdown's own
                  answer to empty content is <code className="not-italic">null</code>.
                </p>
              ) : (
                <div className="space-y-2">
                  <MessageRow role="user" loading={loading}>
                    What can I build with a studio?
                  </MessageRow>
                  <MessageRow role="assistant" loading={loading}>
                    <Markdown content={ANSWER} loading={loading} />
                  </MessageRow>
                </div>
              )}
            </Specimen>

            <Specimen
              name="ChatLog"
              note="initial load — distinct from isLoadingOlder, which sits above real rows"
            >
              <ChatLog
                loading={loading}
                className="h-[190px] rounded-xl border border-border"
                empty={
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    Say hi to Atlas.
                  </div>
                }
              >
                {state === "loaded" ? (
                  <>
                    <DaySeparator label="Today" />
                    <MessageRow role="user">Morning.</MessageRow>
                    <MessageRow role="assistant">
                      Morning. Want to pick up the roadmap where we left it?
                    </MessageRow>
                  </>
                ) : null}
              </ChatLog>
            </Specimen>

            <Specimen
              name="SuggestedReplies"
              note="mixed widths — equal pills would read as a segmented control"
            >
              <SuggestedReplies
                loading={loading}
                replies={
                  state === "loaded"
                    ? ["Show me my top studios", "What can I do here?", "Switch studio"]
                    : []
                }
                onSelect={() => {}}
              />
              {empty ? (
                <p className="text-xs italic text-muted-foreground">
                  Renders nothing — its empty answer is to vanish.
                </p>
              ) : null}
            </Specimen>

            <Specimen
              name="AvatarGroup"
              note="real diameter, ring and overlap, so an inline header doesn't shift"
            >
              <AvatarGroup
                items={members}
                loading={loading}
                empty={<span className="text-sm text-muted-foreground">No members yet.</span>}
              />
            </Specimen>

            <Specimen
              name="SetupChecklist"
              note="holds the card — the empty guard alone would pop it in at full height"
            >
              <SetupChecklist
                tasks={tasks}
                loading={loading}
                celebrateOnComplete={false}
                title="Get set up"
                description="Three steps to your first experience."
              />
              {empty ? (
                <p className="text-xs italic text-muted-foreground">
                  Returns null — the parent owns visibility.
                </p>
              ) : null}
            </Specimen>

            <Specimen
              name="AnimatedList"
              note="items also drain via onDismiss, so empty is a place the user arrived at"
            >
              <AnimatedList
                items={notifications}
                loading={loading}
                autoAddDelay={0}
                height="200px"
                fadeEdges={false}
                startFrom="top"
                empty={<p className="text-sm text-muted-foreground">All caught up.</p>}
              />
            </Specimen>

            <Specimen
              wide
              name="CardGrid"
              note="drops the column track when empty, so the slot isn't pinned to one third of a row"
            >
              <CardGrid<Row>
                items={rows}
                getKey={(r) => r.id}
                renderItem={card}
                loading={loading}
                columns="3"
                // Matches ROWS.length on purpose. CardGrid otherwise defaults
                // this to the LAST known item count, which is right in an app
                // (the next page is usually the size of the last) but has
                // nothing to remember on a first paint.
                skeletonCount={ROWS.length}
                empty={
                  <EmptyState
                    icon={<Inbox className="h-6 w-6" />}
                    title="Nothing here yet."
                    description="First one's the hardest. After that, the rest just show up."
                  />
                }
              />
            </Specimen>

            <Specimen
              wide
              name="Table · TableSkeleton / TableEmpty"
              note="both render real <tr>s inside <TableBody>, so the table keeps one column model"
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableSkeleton columns={4} rows={3} />
                  ) : invoices.length === 0 ? (
                    <TableEmpty colSpan={4}>No invoices yet.</TableEmpty>
                  ) : (
                    invoices.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell className="font-mono text-xs">{inv.id}</TableCell>
                        <TableCell>{inv.plan}</TableCell>
                        <TableCell>{inv.period}</TableCell>
                        <TableCell className="text-right">{inv.amount}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Specimen>
          </div>
        </div>
      </VariantBlock>

      <VariantBlock
        label="Transcript furniture"
        caption="the pieces whose loading answer is NOT the same as their empty answer — each renders nothing when it has nothing, and a placeholder when it has something pending"
      >
        <div className="w-full max-w-2xl space-y-4 rounded-2xl border border-border bg-card p-5">
          <div>
            <p className="mb-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              DaySeparator
            </p>
            <DaySeparator loading={loading} label={state === "loaded" ? "Yesterday" : null} />
            {empty ? (
              <p className="text-xs italic text-muted-foreground">
                Nullish label renders nothing — "no boundary here".
              </p>
            ) : null}
          </div>

          <div>
            <p className="mb-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              MessageTimestamp
            </p>
            <MessageTimestamp
              loading={loading}
              dateTime={state === "loaded" ? "2026-08-25T09:14:00.000Z" : undefined}
              label={state === "loaded" ? "9:14 AM" : undefined}
              absoluteLabel="August 25, 2026 at 9:14 AM"
            />
            {empty ? (
              <p className="text-xs italic text-muted-foreground">
                No dateTime — a still-streaming turn leaves no ghost row.
              </p>
            ) : null}
          </div>

          <div>
            <p className="mb-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              ComposerHint
            </p>
            <ComposerHint loading={loading} tone={state === "loaded" ? "warning" : undefined}>
              {state === "loaded" ? "Attach a file under 20 MB to send." : null}
            </ComposerHint>
            {empty ? (
              <p className="text-xs italic text-muted-foreground">
                Renders nothing visible — but KEEPS its live region mounted, which is what lets the
                next real hint be announced at all.
              </p>
            ) : null}
          </div>

          <div>
            <p className="mb-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              ListeningWave · connecting
            </p>
            <ListeningWave
              connecting={loading}
              transcript={state === "loaded" ? "Draft a roadmap for the podcast studio" : ""}
              maxHeight={96}
              onStop={() => {}}
            />
            <p className="mt-1 text-xs italic text-muted-foreground">
              {loading
                ? "The meter holds STILL — an animated meter with no audio behind it misreports the microphone."
                : empty
                  ? 'Mic is live with nothing heard yet, so the placeholder "Listening…" is a true statement.'
                  : "Live transcript."}
            </p>
          </div>
        </div>
      </VariantBlock>
    </Section>
  );
}
